import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';

const BACKEND_URL = (() => {
  const raw =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL_DEV ||
    'http://localhost:4200';
  return raw.replace(/\/api\/v1\/?$/, '');
})();

/**
 * Hook that connects to the Standby WebSocket and joins a team room.
 * When the backend emits team:updated, invalidates the team reviews query.
 */
export function useTeamSocket(teamId: string | number | undefined) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!teamId) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-team', +teamId);
    });

    const invalidateTeam = () => {
      queryClient.invalidateQueries({ queryKey: ['team', String(teamId)] });
      queryClient.invalidateQueries({ queryKey: ['team-reviews', String(teamId)] });
      queryClient.invalidateQueries({ queryKey: ['my-teams'] });
      queryClient.invalidateQueries({ queryKey: ['team-invites', +teamId] });
    };

    socket.on('team:updated', invalidateTeam);

    socket.on('reconnect', () => {
      socket.emit('join-team', +teamId);
      invalidateTeam();
    });

    // Safety-net polling in case socket is silently dead
    const fallbackInterval = setInterval(invalidateTeam, 15000);

    return () => {
      clearInterval(fallbackInterval);
      socket.emit('leave-team', +teamId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [teamId, queryClient]);

  return socketRef;
}
